import {
	beginImpersonation,
	initAnalytics,
	isEnabled,
	trackStoreAction,
	shouldTrackAction,
	currentErrorSeq,
} from "../utils/analytics"
import { readImpersonation } from "../utils/impersonation"

/**
 * Mixpanel bootstrap. Everything about *what* gets tracked lives in
 * `utils/analytics.ts`; this file only wires the SDK into the Nuxt app.
 *
 * The environment gate is inside `initAnalytics()`: tracking is off unless the
 * app is pointed at the production API, or VITE_MIXPANEL_DEBUG=true. When it's
 * off the whole facade no-ops, so no events queue up and nothing throws.
 */
export default defineNuxtPlugin((nuxtApp) => {
	if (!initAnalytics()) return

	// --- Impersonation: keep the admin's identity, never the target's ---
	const impersonation = readImpersonation()
	if (impersonation) {
		// Same single call the loginAs* actions make, so the reload and SPA paths
		// cannot drift apart in what they label.
		beginImpersonation(impersonation)
	}

	// --- Tier 3: blanket write telemetry ---
	//
	// Registered on `app:mounted` rather than at plugin setup because plugins
	// load alphabetically and `plugins/vuex.ts` (which installs the store) sorts
	// after this file — $store does not exist yet during setup.
	nuxtApp.hook("app:mounted", () => {
		const { $store } = useNuxtApp() as any
		if (!$store || !isEnabled()) return

		const started = new Map<string, number>()
		const errorSeqAt = new Map<string, number>()

		$store.subscribeAction({
			before: (action: any) => {
				if (!shouldTrackAction(action.type)) return
				// One timestamp per action type is enough: concurrent dispatches of
				// the same write action are vanishingly rare in the hub, and an
				// overwrite just means the later one wins.
				started.set(action.type, Date.now())
				errorSeqAt.set(action.type, currentErrorSeq())
			},
			after: (action: any) => {
				if (!shouldTrackAction(action.type)) return

				const startedAt = started.get(action.type)
				if (startedAt === undefined) return
				started.delete(action.type)

				// An error banner raised during this action's window means it failed,
				// even though the dispatch resolved. Heuristic — a concurrent action's
				// failure could be attributed here — but far better than counting every
				// swallowed failure as a completion.
				const seqBefore = errorSeqAt.get(action.type)
				errorSeqAt.delete(action.type)
				const outcome =
					seqBefore !== undefined && currentErrorSeq() > seqBefore ? "failed" : "success"

				trackStoreAction(action.type, Date.now() - startedAt, useRoute()?.name as string, outcome)
			},
			error: (action: any) => {
				if (!shouldTrackAction(action.type)) return
				started.delete(action.type)
				errorSeqAt.delete(action.type)
			},
		})
	})
})
